/** Whether this window can capture the screen (a browser asks the user what to share). */
export function screenCaptureSupported(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    typeof navigator.mediaDevices?.getDisplayMedia === 'function'
  );
}

/** Whether a failed capture is the user closing the picker (nothing to report). */
export function screenshotCancelled(reason: unknown): boolean {
  // Chromium rejects a closed picker with "Permission denied by user"; a refusal of the system
  // (no screen recording permission, no handler) is a plain "Permission denied", worth saying.
  return (
    reason instanceof DOMException &&
    (reason.name === 'AbortError' ||
      (reason.name === 'NotAllowedError' && /by user/i.test(reason.message)))
  );
}

/**
 * One picture of what the user chooses to share (a screen, a window, a tab), as a PNG file to
 * attach to the message. Rejects when the user cancels.
 */
export async function captureScreenshot(): Promise<File> {
  const stream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: false });
  try {
    const video = document.createElement('video');
    video.muted = true;
    video.srcObject = stream;
    await video.play();
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d')?.drawImage(video, 0, 0);
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
    if (blob === null) throw new Error('The screenshot could not be taken.');
    const stamp = new Date().toISOString().slice(0, 19).replace('T', ' ').replaceAll(':', '.');
    return new File([blob], `Screenshot ${stamp}.png`, { type: 'image/png' });
  } finally {
    for (const track of stream.getTracks()) track.stop();
  }
}
